import { ReactElement } from "react";
import {
	DocumentGlobalSearchResultItem,
	GlobalFileSearchResultModel,
	GlobalSearchResultItemKind,
	GlobalSearchScopeIdentifier
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { never } from "client_legacy/ndaw/errors";
import { ISearchResultItem } from "./MultiScopeSearch";
import { TimeZone } from "client/DateAndTime/timezones";
import { ContactSource } from "client/Api/Email/contacts";
import { AppItemView } from "../../Items/AppItemView";
import { ContactItemView } from "../../Items/ContactItemView";
import { DocumentItemView } from "../../Items/DocumentItemView";
import { EmailItemView } from "../../Items/EmailItemView";
import { AnnouncementItemView } from "../../Items/AnnouncementItemView";
import { KnowledgeItemView } from "../../Items/KnowledgeItemView";
import { EgnyteItemView } from "../../Items/EgnyteItemView";
import { EventItemView } from "../../Items/EventItemView";
import { IntegrationFlowItemView } from "../../Items/IntegrationFlowItemView";
import { CommunityItemView } from "../../Items/CommunityItemView";
import { CommunityPostItemView } from "../../Items/CommunityPostItemView";
import { isNotNil } from "client/Misc/utils";

export type ItemViewProps = {
	item: ISearchResultItem;
	itemsScope: GlobalSearchScopeIdentifier;
	index: number;
	navigationIndex: number;
	isMobile: boolean;
	timezone: TimeZone;
	onOpenContactDetails: (id: string, source: ContactSource) => void;
};

export const ItemView = (props: ItemViewProps): ReactElement => {
	const {
		item,
		itemsScope,
		index,
		navigationIndex,
		isMobile,
		timezone,
		onOpenContactDetails
	} = props;

	switch (item.kind) {
		case GlobalSearchResultItemKind.App:
			return (
				<AppItemView
					item={item}
					navigationIndex={navigationIndex}
					itemScope={itemsScope}
					timezone={timezone}
				/>
			);
		case GlobalSearchResultItemKind.Contact:
			return (
				<ContactItemView
					item={item}
					navigationIndex={navigationIndex}
					itemScope={itemsScope}
					onOpenContactDetails={onOpenContactDetails}
				/>
			);
		case GlobalSearchResultItemKind.Document:

			return (
				<DocumentItemView
					item={item}
					content={
						hasContent(item)
							? item.content
							: undefined
					}
					navigationIndex={navigationIndex}
					index={index}
					itemScope={itemsScope}
					isMobile={isMobile}
					timezone={timezone}
				/>
			);
		case GlobalSearchResultItemKind.Email:
			return (
				<EmailItemView
					item={item}
					navigationIndex={navigationIndex}
					itemScope={itemsScope}
					timezone={timezone}
				/>
			);
		case GlobalSearchResultItemKind.Announcement:
			return (
				<AnnouncementItemView
					item={item}
					navigationIndex={navigationIndex}
					itemScope={itemsScope}
					isMobile={isMobile}
					timezone={timezone}
				/>
			);
		case GlobalSearchResultItemKind.KnowledgeItems:
			return (
				<KnowledgeItemView
					item={item}
					navigationIndex={navigationIndex}
					itemScope={itemsScope}
					isMobile={isMobile}
					timezone={timezone}
				/>
			);
		case GlobalSearchResultItemKind.EgnyteItem:
			return (
				<EgnyteItemView
					item={item}
					navigationIndex={navigationIndex}
					itemScope={itemsScope}
					timezone={timezone}
				/>
			);
		case GlobalSearchResultItemKind.Event:
			return (
				<EventItemView
					item={item}
					navigationIndex={navigationIndex}
					itemScope={itemsScope}
					isMobile={isMobile}
					timezone={timezone}
				/>
			);
		case GlobalSearchResultItemKind.IntegrationFlow:
			return (
				<IntegrationFlowItemView
					item={item}
					navigationIndex={navigationIndex}
					index={index}
					itemScope={itemsScope}
					timezone={timezone}
				/>
			);
		case GlobalSearchResultItemKind.Community:
			return (
				<CommunityItemView
					item={item}
					navigationIndex={navigationIndex}
					itemScope={itemsScope}
				/>
			);
		case GlobalSearchResultItemKind.CommunityPost:
			return (
				<CommunityPostItemView
					item={item}
					navigationIndex={navigationIndex}
					itemScope={itemsScope}
					timezone={timezone}
					isMobile={isMobile}
				/>
			);
		default:
			throw never(item);
	}
};

ItemView.displayName = "ItemView";

function hasContent(item: DocumentGlobalSearchResultItem | GlobalFileSearchResultModel)
	: item is GlobalFileSearchResultModel {

	return isNotNil((item as GlobalFileSearchResultModel).content);
}